import type { DisplayCreative, TTDCampaign, User } from '@feathr/blackbox';
import { validateStepCreatives } from '@feathr/extender/components/creatives/dimensionValidation.utils';
import { getEffectiveCreativeDestinationUrls } from '@feathr/extender/components/Goal/Goal.predicates';
import { getEphemeralGeoTargetables } from '@feathr/extender/components/Targetings';
import { useBlackboxVersion, useCampaignChannelVisibility } from '@feathr/extender/hooks';
import { useAccount, useFlags, useStore, useUser } from '@feathr/extender/state';
import { flattenErrors } from '@feathr/hooks';
import type { ListResponse, Model } from '@feathr/rachis';
import { type IUseAudienceBreakdown, useAudienceBreakdown } from '@feathr/report_components';

import type { ICampaignValidationErrors } from '../../CampaignSummary';
import type { IEditedGoalIds } from '../StepGoals';
import { getGoalSegments, validateStepGoals } from '../StepGoals';
import { type IValidateStepGeneralConfig, validateStepGeneral } from '../WizardGeneralStep';
import {
  type IUniqueEmailsResult,
  useReconcileUniqueEmails,
} from './AddWizard.useReconcileUniqueEmails';
import type { ICampaignDetails } from './AdWizard';
import {
  getEphemeralTargetables,
  getTargetSegments,
  type IReachValidationResult,
  type IUseEstimatedReach,
  useEstimatedReach,
  useReachValidation,
  validateStepTargets,
} from './AdWizardAudienceStep';
import { validateStepBudget } from './AdWizardBudgetStep';
import { validateStepFilters } from './AdWizardFiltersStep';
import { validateStepOptimize } from './AdWizardOptimizationStep';
import { validatePartner } from './AdWizardReviewStep/AdWizardReviewStep';
interface IUseCampaignValidationProps {
  campaign: TTDCampaign;
  /** Edited-goal IDs, threaded so save/validate enforces the creative-match rule on the same goals as the Next/Publish gate. */
  editedGoalIds?: IEditedGoalIds;
}

interface IUseCampaignValidation {
  getValidationErrors: () => ICampaignValidationErrors;
  grandchildModels: Model[];
  childModels: Model[];
  emailValidation: IUniqueEmailsResult;
  reachValidation: IReachValidationResult;
  audienceData: IUseAudienceBreakdown;
  estimatedReach: IUseEstimatedReach;
}

/** Validate all of the fields in the AdWizard. */
function useCampaignValidation({
  campaign,
  editedGoalIds,
}: IUseCampaignValidationProps): IUseCampaignValidation {
  const { Creatives, Billables, Events, Goals, Segments, Targetings, Targetables, Partners } =
    useStore();
  const account = useAccount();
  const user = useUser();
  const flags = useFlags();
  const { isOnProduction } = useBlackboxVersion();
  const { showMeta } = useCampaignChannelVisibility(campaign);

  const partnerId = campaign.isMonetization ? campaign.get('parent') : undefined;
  const partner = partnerId ? Partners.get(partnerId) : undefined;
  const event = Events.get(campaign.get('event'));
  const billing = event.get('billing');
  const billable =
    !event.isPending && billing && billing.billable_id
      ? Billables.get(billing.billable_id)
      : undefined;

  const targetings = Targetings.list({
    filters: {
      _parent: campaign.id,
      is_archived__ne: true,
    },
    pagination: { page_size: 1000 },
  });

  const emailValidation = useReconcileUniqueEmails({
    targetings: targetings.models,
    campaign,
  });

  // Single shared audience breakdown for the whole wizard — display, alerts, and the reach gate.
  const audienceData = useAudienceBreakdown({ targetings, campaign, showMeta });
  // Single shared reach forecast for the whole wizard — the display estimate and the reach gate
  // consume this one fetch rather than each hitting the forecast endpoint independently.
  const estimatedReach = useEstimatedReach({ targetings, campaign });
  const reachValidation = useReachValidation({
    campaign,
    targetings,
    audienceData,
    estimatedReach,
  });

  const goals = Goals.list({
    filters: {
      _parent: campaign.id,
      is_archived__ne: true,
    },
  });
  // Campaigns only have DisplayCreatives.
  const creatives = Creatives.list({
    filters: {
      _parent: campaign.id,
      is_archived__ne: true,
    },
    ordering: ['-date_created'],
  }) as ListResponse<DisplayCreative>;

  const grandchildModels: Model[] = [
    ...getTargetSegments(targetings.models, Segments),
    ...getGoalSegments(goals.models, Segments),
    ...getEphemeralGeoTargetables(targetings.models, Targetables),
  ];

  // Converting observables back to vanilla JavaScript.
  const childModels: Model[] = [
    ...getEphemeralTargetables(targetings.models, Targetables, campaign),
    ...targetings.models.slice(),
    ...creatives.models.slice(),
    ...goals.models.slice(),
  ];

  function validate(
    details: ICampaignDetails,
    user?: User,
    noMaxBudget?: boolean,
  ): ICampaignValidationErrors {
    const {
      campaign,
      account,
      creatives,
      goals,
      goalSegments,
      targetables,
      targetings,
      targetingSegments,
      partner,
      emailValidation,
      creativeDestinationUrls,
      editedGoalIds,
      reachValidation,
    } = details;

    const generalConfig: IValidateStepGeneralConfig = {
      isOmniCampaign: campaign.isOmniCampaign,
    };
    const generalStepValidation = validateStepGeneral(campaign, generalConfig);
    const targetsStepValidation = flattenErrors(
      validateStepTargets({
        campaign,
        targetings,
        segments: targetingSegments ?? [],
        targetables: targetables ?? [],
        emailValidation,
        reachValidation,
        isReachMinimumExempt: account.isInternal && !isOnProduction,
      }),
    );
    const filtersStepValidation = validateStepFilters(campaign, targetings);
    const creativesStepValidation = validateStepCreatives(campaign, creatives);
    const budgetStepValidation = validateStepBudget({
      campaign,
      account,
      user,
      noMaxBudget,
      isOnProduction,
    });
    const optimizationStepValidation = flattenErrors(validateStepOptimize(campaign));
    const goalsStepValidation = validateStepGoals({
      goals,
      goalSegments,
      creativeDestinationUrls,
      editedGoalIds,
      isDraftCampaign: campaign.isDraft,
    });

    const monetizationStepValidation = [
      ...(budgetStepValidation.monetization_value ?? []),
      ...(partner ? validatePartner(partner) : []),
    ];

    const validationErrors: ICampaignValidationErrors = {
      name: generalStepValidation,
      targets: targetsStepValidation,
      filters: filtersStepValidation,
      creatives: creativesStepValidation,
      duration: [
        ...(budgetStepValidation.date_start ?? []),
        ...(budgetStepValidation.date_end ?? []),
      ],
      optimization: optimizationStepValidation,
      budget: budgetStepValidation.budget,
      channel_split: budgetStepValidation.channel_split,
      monetization: monetizationStepValidation,
      goals: goalsStepValidation.goals,
    };

    return validationErrors;
  }

  function getValidationErrors(): ICampaignValidationErrors {
    const targetingSegments = getTargetSegments(targetings.models, Segments);
    const targetables = getEphemeralTargetables(targetings.models, Targetables, campaign);
    const goalSegments = getGoalSegments(goals.models, Segments);
    return validate(
      {
        account,
        billable,
        campaign,
        creatives: creatives.models,
        event,
        goals: goals.models,
        goalSegments,
        targetables,
        targetings: targetings.models,
        targetingSegments,
        partner,
        emailValidation,
        creativeDestinationUrls: getEffectiveCreativeDestinationUrls(
          creatives.models,
          campaign.get('destination_url'),
        ),
        editedGoalIds,
        reachValidation,
      },
      user,
      flags.noMaxBudget,
    );
  }

  return {
    getValidationErrors,
    grandchildModels,
    childModels,
    emailValidation,
    reachValidation,
    audienceData,
    estimatedReach,
  };
}

export default useCampaignValidation;
